import styled from 'styled-components';

import Menu from 'components/shared/menu';
import { mobileTouchExpand } from 'components/shared/responsiveness/touchTarget';
import Activity from 'models/Activity';
import { useArchiveWorkflowActionActivity } from 'services/api/workflowActionActivities';
import { colors } from 'styles/theme/colors';
import CrissCross from 'svg/CrissCross';
import MenuHorizontalIcon from 'svg/MenuHorizontalIcon';
import TrashIcon from 'svg/TrashIcon';

/**
 * The per-note kebab. Only ever rendered for the note's author.
 *
 * Standalone rather than `ActivityCardMenu`, which is coupled to DischargeQuestions and
 * ProgressUpdate, carries an `& Attachments` copy branch that can never apply here, and calls
 * useUpdateActivity. The mock also wants different copy and different icons: "Delete" in red
 * with a trash, "Undo Delete" in normal text with a restore.
 *
 * Deleting is an archive, not a destroy — the strikethrough-and-undo behaviour falls out of it.
 */
export default function NoteMenu({
  note,
  workflowId,
  actionId,
}: {
  note: Activity;
  workflowId: string;
  actionId: string;
}) {
  const { mutate } = useArchiveWorkflowActionActivity(workflowId, actionId);

  const toggleArchived = () => mutate({ id: note.id, archived: !note.archived });

  return (
    <Menu>
      <Menu.Trigger>
        <Trigger data-testid='note-menu-trigger'>
          <MenuHorizontalIcon />
        </Trigger>
      </Menu.Trigger>
      <Menu.Content position='right'>
        <Menu.Item onClick={toggleArchived} data-testid='note-menu-toggle'>
          {note.archived ? (
            <NeutralLabel>
              <CrissCross width={16} height={13} />
              Undo Delete
            </NeutralLabel>
          ) : (
            <DestructiveLabel>
              <TrashIcon width={18} height={18} />
              Delete
            </DestructiveLabel>
          )}
        </Menu.Item>
      </Menu.Content>
    </Menu>
  );
}

const Trigger = styled.div`
  /* The row's only write path, and a bare glyph at about 16px. The area grows rather than the
     control, since the kebab's size is the design. */
  ${mobileTouchExpand}

  cursor: pointer;
`;

const Label = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const NeutralLabel = styled(Label)`
  color: ${colors.black100};
`;

// TrashIcon strokes with currentColor, so colouring the label colours the glyph too.
const DestructiveLabel = styled(Label)`
  color: ${colors.red100};
`;
